<script setup lang="ts">
import { refDebounced } from '@vueuse/core'
import { ref, watch } from 'vue'
import { useDisplay } from 'vuetify'
import ConfirmDialog from '@/components/dialogs/ConfirmDialog.vue'
import CustomCloseButton from '@/components/createdUi/CustomCloseButton.vue'
import DocumentTypeModal from './DocumentTypeModal.vue'
import { ReferencesApi } from '@/modules/documents-new/services/referencesApi'
import type { DocumentType } from '@/modules/documents-new/types/document.types'
import { useNotifications } from '@/modules/feedback/composables/useNotifications'

interface Props {
  isOpen: boolean
}

interface Emits {
  (e: 'update:isOpen', value: boolean): void
}

const props = defineProps<Props>()
const emit = defineEmits<Emits>()

const { smAndDown } = useDisplay()
const { showSuccess, showError } = useNotifications()

const types = ref<DocumentType[]>([])
const isLoading = ref(false)
const search = ref('')
const searchDebounced = refDebounced(search, 300)
const isFormOpen = ref(false)
const editingType = ref<DocumentType | null>(null)
const isDeleteConfirmOpen = ref(false)
const typeToDelete = ref<DocumentType | null>(null)
const isDeleting = ref(false)
let loadSeq = 0

const loadTypes = async () => {
  const seq = ++loadSeq
  const query = searchDebounced.value.trim()
  isLoading.value = true
  try {
    const next = await ReferencesApi.fetchTypes({
      ordering: 'name',
      ...(query ? { search: query } : {}),
    })
    if (seq !== loadSeq)
      return
    types.value = next
  } catch (err) {
    if (seq !== loadSeq)
      return
    showError(err, 'Загрузка видов документов')
  } finally {
    if (seq === loadSeq)
      isLoading.value = false
  }
}

watch(
  () => [props.isOpen, searchDebounced.value] as const,
  async ([open]) => {
    if (!open) {
      if (search.value)
        search.value = ''
      return
    }

    await loadTypes()
  },
)

const closeModal = () => {
  emit('update:isOpen', false)
}

const openCreate = () => {
  editingType.value = null
  isFormOpen.value = true
}

const openEdit = (item: DocumentType) => {
  editingType.value = item
  isFormOpen.value = true
}

const onTypeSaved = async () => {
  await loadTypes()
}

const requestDelete = (item: DocumentType) => {
  typeToDelete.value = item
  isDeleteConfirmOpen.value = true
}

const handleDeleteConfirm = async (confirmed: boolean) => {
  isDeleteConfirmOpen.value = false
  if (!confirmed || !typeToDelete.value) {
    typeToDelete.value = null
    return
  }

  isDeleting.value = true
  try {
    await ReferencesApi.deleteType(typeToDelete.value.id)
    showSuccess('Вид документа удалён')
    await loadTypes()
  } catch (err) {
    showError(err, 'Удаление вида документа')
  } finally {
    isDeleting.value = false
    typeToDelete.value = null
  }
}
</script>

<template>
  <VDialog
    :model-value="isOpen"
    :max-width="smAndDown ? '100%' : 720"
    :fullscreen="smAndDown"
    persistent
    @update:model-value="emit('update:isOpen', $event)"
  >
    <VCard :class="smAndDown ? 'pa-4' : 'pa-6'">
      <div class="d-flex justify-space-between align-center mb-4">
        <span class="text-h5 font-weight-bold">Виды документов</span>
        <CustomCloseButton :close-action="closeModal" />
      </div>

      <div
        class="d-flex align-center ga-3 mb-4"
        :class="{ 'flex-column align-stretch': smAndDown }"
      >
        <VTextField
          v-model="search"
          variant="outlined"
          density="compact"
          prepend-inner-icon="ri-search-line"
          placeholder="Поиск"
          hide-details
          clearable
          class="flex-grow-1"
        />
        <VBtn
          color="primary"
          prepend-icon="ri-add-line"
          :block="smAndDown"
          @click="openCreate"
        >
          Добавить
        </VBtn>
      </div>

      <div
        v-if="isLoading"
        class="d-flex justify-center py-8"
      >
        <VProgressCircular
          indeterminate
          color="primary"
          size="32"
        />
      </div>

      <div
        v-else-if="types.length === 0"
        class="text-center text-medium-emphasis py-8"
      >
        Виды документов не найдены
      </div>

      <VList
        v-else
        class="types-list"
        density="compact"
      >
        <VListItem
          v-for="item in types"
          :key="item.id"
          class="types-list-item"
          @click="openEdit(item)"
        >
          <VListItemTitle class="font-weight-medium">
            {{ item.name }}
          </VListItemTitle>
          <VListItemSubtitle v-if="item.description">
            {{ item.description }}
          </VListItemSubtitle>

          <template #append>
            <div
              class="d-flex align-center ga-1"
              @click.stop
            >
              <VIconBtn
                icon="ri-edit-line"
                variant="text"
                color="secondary"
                size="small"
                @click="openEdit(item)"
              />
              <VIconBtn
                icon="ri-delete-bin-line"
                variant="text"
                color="secondary"
                size="small"
                :loading="isDeleting && typeToDelete?.id === item.id"
                @click="requestDelete(item)"
              />
            </div>
          </template>
        </VListItem>
      </VList>
    </VCard>
  </VDialog>

  <DocumentTypeModal
    v-model:is-open="isFormOpen"
    :document-type="editingType"
    @saved="onTypeSaved"
  />

  <ConfirmDialog
    v-model:is-dialog-visible="isDeleteConfirmOpen"
    confirmation-question="Удалить вид документа?"
    :confirmation-text="typeToDelete ? `«${typeToDelete.name}» будет удалён. Удаление невозможно, если есть привязанные документы.` : ''"
    confirm-btn="Удалить"
    reject-btn="Отменить"
    @confirm="handleDeleteConfirm"
  />
</template>

<style scoped lang="scss">
:deep(.v-card) {
  border-radius: 12px;
}

:deep(.v-btn) {
  text-transform: none;
  font-weight: 500;
}

.types-list {
  max-height: min(60vh, 480px);
  overflow-y: auto;
  border: 1px solid rgba(var(--v-theme-on-surface), 0.12);
  border-radius: 8px;
  padding: 0;
}

.types-list-item {
  border-bottom: 1px solid rgba(var(--v-theme-on-surface), 0.08);

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: rgba(var(--v-theme-on-surface), 0.04);
  }
}
</style>
